/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import { get, post, BASE } from './client.js'

// ─── Billing (contratação — features/billing) ─────────────────────────────────

export type PlanDto = {
  code: string
  name: string
  description: string | null
  price_cents: number
  currency: string
  stripe_price_id: string | null
  max_kitchen: number | null
  max_waiters: number | null
  /** Gerentes (#286): null = ilimitado; 0 = sem perfil Gerente */
  max_managers: number | null
  translation_locales: number | null
  menu_import_ocr: boolean
  is_active: boolean
  sort_order: number
  /** #293: 'bundle' | 'plan' */
  kind?: string
  /** #293: só o bundle tem checkout self-serve */
  self_serve?: boolean
  /** #293: preço exibido publicamente (Small/Pro: não) */
  public_price?: boolean
  requires_homologated_country?: boolean
}

export const billing = {
  /** Plano efetivo do tenant atual + estado da assinatura. */
  subscription: () =>
    get<{
      subscription: {
        plan_code: string
        status: string
        current_period_end: number | null
        stripe_customer_id?: string | null
        trial_end?: number | null
      }
      plan: PlanDto
      /** #293: o que está liberado agora (trial, carência, país) */
      entitlements?: Entitlements
    }>('/billing/subscription'),
  /** Checkout do Stripe para contratar/fazer upgrade de plano. */
  checkout: (planCode: string) =>
    post<{ url: string; session_id: string }>('/billing/checkout', { plan_code: planCode }),
  /** Portal do Stripe: trocar plano / cancelar (com proração). */
  portal: () => post<{ url: string }>('/billing/portal', {}),
}

/** #293 — entitlements do tenant (GET /billing/subscription). */
export type Entitlements = {
  plan_code: string
  status: string
  access: 'trial' | 'active' | 'grace' | 'inactive'
  trial_end: number | null
  grace_until: number | null
  country_code: string | null
  homologated_full: boolean
  features: string[]
  public_menu: boolean
}

/** #293 — países (admin da plataforma). */
export type CountryDto = {
  code: string
  name: string
  enabled_signup: boolean
  homologated_full: boolean
  updated_at: number
}

export type LeadStatus = 'new' | 'contacted' | 'closed' | 'spam'
export type LeadPlan = 'small_business' | 'pro' | 'notify'

/** #293 — lead de contato (admin da plataforma). */
export type LeadDto = {
  id: string
  plan: LeadPlan
  company_name: string | null
  contact_name: string
  email: string
  country: string
  detected_country: string | null
  details: string | null
  locale: string
  consent_text_version: string
  consent_at: number
  source_page: string
  status: LeadStatus
  notified_at: number | null
  created_at: number
}

export type BillingSettingsDto = {
  trial_days: number
  past_due_grace_days: number
  default_plan_code: string
  existing_tenants_new_trial: boolean
  trial_once_per_tenant: boolean
  trial_once_per_owner_email: boolean
}

/** #293 — POST /public/leads (contrato da landing; sem tenant nem auth). */
export type LeadPayload = {
  plan: LeadPlan
  company_name?: string
  contact_name: string
  email: string
  country: string
  details?: string
  locale: string
  consent: { text_version: string; at: string }
  source_page: string
  turnstile_token: string
  website?: string
}

export type LeadResult =
  | { ok: true; id: string }
  | {
      ok: false
      status: number
      code: string
      fields?: Record<string, string>
      retryAfter?: number
    }

export const leadsApi = {
  /** GET /public/countries (#293): ISO2 com cadastro aberto / versão completa. */
  countries: async (): Promise<{ homologated: string[]; signup: string[] }> => {
    const res = await fetch(`${BASE}/public/countries`)
    if (!res.ok) throw new Error(`countries ${res.status}`)
    return (await res.json()) as { homologated: string[]; signup: string[] }
  },
  create: async (payload: LeadPayload): Promise<LeadResult> => {
    const res = await fetch(`${BASE}/public/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const body = (await res.json().catch(() => ({}))) as {
      ok?: boolean
      data?: { id: string }
      error?: { code?: string; details?: { fields?: Record<string, string> } }
    }
    if (res.status === 201 && body.data) return { ok: true, id: body.data.id }
    const retry = Number(res.headers.get('Retry-After'))
    return {
      ok: false,
      status: res.status,
      code: body.error?.code ?? 'UNKNOWN',
      ...(body.error?.details?.fields ? { fields: body.error.details.fields } : {}),
      ...(Number.isFinite(retry) && retry > 0 ? { retryAfter: retry } : {}),
    }
  },
}

/** Fila de chamadas de garçom (salão). */
export type WaiterCall = {
  id: string
  table_id: string
  table_number: number
  table_label: string | null
  called_at: number
  attended_at: number | null
}

export const waiterCallsApi = {
  list: () => get<WaiterCall[]>('/orders/waiter-calls'),
  attend: (id: string) => post<{ attended_at: number }>(`/orders/waiter-calls/${id}/attend`, {}),
}

/** Cardápio público — usado pelo garçom para montar pedidos. O idioma do
 *  painel (sr_admin_lang, escolhido no seletor da sidebar) é injetado como
 *  locale default: os itens seguem a tradução que o usuário está vendo. */
export const publicMenu = {
  categories: (channel: 'dine_in' | 'takeaway' | 'delivery', locale?: string) => {
    const panelLang =
      locale ?? (typeof window !== 'undefined' ? localStorage.getItem('sr_admin_lang') : null)
    const loc = panelLang ? `&locale=${encodeURIComponent(panelLang)}` : ''
    return get<PublicMenuCategory[]>(`/menu/categories?channel=${channel}${loc}`)
  },
}

export type PublicMenuCategory = {
  id: string
  name: string
  products: PublicMenuProduct[]
}

export type PublicMenuProduct = {
  id: string
  name: string
  price_cents: number
  description: string | null
  ingredients: Array<{ id: string; name: string; is_removable: boolean }>
  extras: Array<{
    id: string
    name: string
    price_type: 'fixed' | 'percent'
    price_value: number
  }>
  variant_group_label: string | null
  variants: Array<{ id: string; name: string; price_cents: number }>
}
